import { ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EMPTY_RECEIPT, STATUSES } from "@/lib/store";
import { COLORS, FOLDER_COLORS } from "@/lib/calendar";
import { ColorSwatches } from "./ColorSwatches";

export const HEALTH_TAGS = ["Symptoms", "Diet", "Scans", "Medication", "Exercise", "Sleep", "Mood", "Lab Results"];

function Field({ label, testid, className = "", ...p }) {
  return (
    <div className={`space-y-1 min-w-0 ${className}`}>
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Input className="h-9" data-testid={testid} {...p} />
    </div>
  );
}

const linkOf = (s = "") => (/^https?:\/\//i.test(s) ? s : /^www\./i.test(s) ? `https://${s}` : null);

function Fields({ draft, set }) {
  const r = { ...EMPTY_RECEIPT, ...draft.receipt };
  const setR = (k) => (e) => set({ receipt: { ...r, [k]: e.target.value } });
  const f = draft.folderId;
  if (f === "daily-journal") return (
    <Field className="sm:col-span-2 lg:col-span-4" label="Context (optional)" testid="note-context-input" value={draft.context || ""} onChange={(e) => set({ context: e.target.value })} placeholder="e.g., Morning reflection, Studio session" />
  );
  if (f === "receipts-tracking") return (
    <>
      <Field label="Vendor" testid="receipt-vendor-input" value={r.vendor} onChange={setR("vendor")} placeholder="e.g. Home Depot" />
      <Field label="Amount $" testid="receipt-amount-input" value={r.amount} onChange={setR("amount")} placeholder="0.00" inputMode="decimal" />
      <Field label="Invoice / Receipt #" testid="receipt-invoice-input" value={r.invoice} onChange={setR("invoice")} placeholder="e.g. INV-55821" />
      <Field label="Purchase date" testid="receipt-date-input" type="date" value={r.date} onChange={setR("date")} />
    </>
  );
  if (f === "proof-of-service") return (
    <>
      <Field label="Courier / Carrier" testid="receipt-carrier-input" value={r.carrier} onChange={setR("carrier")} placeholder="e.g. UPS, FedEx, USPS" />
      <Field className="lg:col-span-2" label="Tracking #" testid="receipt-tracking-input" value={r.tracking} onChange={setR("tracking")} placeholder="e.g. 1Z999AA1..." />
      <div className="space-y-1 min-w-0">
        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Delivery status</Label>
        <Select value={r.status || "none"} onValueChange={(v) => set({ receipt: { ...r, status: v === "none" ? "" : v } })}>
          <SelectTrigger className="h-9" data-testid="receipt-status-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="none">No status</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </>
  );
  if (f === "appointments") {
    const href = linkOf(draft.location);
    return (
      <>
        <Field label="Date & Time" testid="appointment-datetime-input" type="datetime-local" value={draft.apptAt || ""}
          onChange={(e) => set({ apptAt: e.target.value, scheduledFor: e.target.value.slice(0, 10), entryType: "Appointment" })} />
        <div className="sm:col-span-1 lg:col-span-3 flex items-end gap-1.5 min-w-0">
          <Field className="flex-1" label="Location / Link (optional)" testid="appointment-location-input" value={draft.location || ""} onChange={(e) => set({ location: e.target.value })} placeholder="e.g. 200 Main St, or a meeting link" />
          {href && <a href={href} target="_blank" rel="noreferrer" className="h-9 w-9 shrink-0 rounded-md border flex items-center justify-center hover:bg-muted" title="Open link" data-testid="appointment-open-link"><ExternalLink className="h-4 w-4" /></a>}
        </div>
      </>
    );
  }
  if (f === "dispute-legal-log") return (
    <>
      <Field className="sm:col-span-2 lg:col-span-4" label="Claim # / File #" testid="note-claim-input" value={draft.ref} onChange={(e) => set({ ref: e.target.value })} placeholder="e.g. CLM-2026-0042" />
    </>
  );
  if (f === "health") {
    const tags = draft.tags || [];
    const toggle = (t) => set({ tags: tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t] });
    return (
      <>
        <div className="sm:col-span-2 lg:col-span-4 space-y-1">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Categories</Label>
          <div className="flex flex-wrap gap-1.5">
            {HEALTH_TAGS.map((t) => (
              <button key={t} type="button" onClick={() => toggle(t)} data-testid={`health-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className={`h-8 px-3 rounded-full border text-xs transition-colors ${tags.includes(t) ? "bg-foreground text-background border-foreground" : "hover:bg-muted"}`}>{t}</button>
            ))}
          </div>
        </div>
      </>
    );
  }
  return <Field className="sm:col-span-2 lg:col-span-4" label="Reference / File # (optional)" testid="note-ref-input" value={draft.ref} onChange={(e) => set({ ref: e.target.value })} placeholder="e.g. REF-1042" />;
}

export function MetaBar({ draft, set }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-3 sm:p-4 space-y-3" data-testid="meta-bar">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`meta-fields-${draft.folderId}`}><Fields draft={draft} set={set} /></div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Calendar color</span>
        <ColorSwatches value={draft.color || ""} onChange={(color) => set({ color })} autoColor={COLORS[FOLDER_COLORS[draft.folderId] || "teal"]} testPrefix="note-color" />
      </div>
    </div>
  );
}
